import { useState, useEffect, useCallback } from "react";
import {
  I,
  SK,
  SCHED_DAYS,
  normalizeWeeklySchedule,
  schedDayIndex,
  slotToTime,
  pname,
  queueScrollToEv,
} from "../utils";
import { LockButton, InitialsBadge } from "../components/SharedUI";
import CallAvatar from "../components/CallAvatar";
import ScheduleGrid from "./ScheduleGrid";
import { usePermissions } from "../hooks/usePermissions";
import { useTaxonomyList } from "../hooks/useTaxonomy";
import { getWorkLocationMeta, workLocationCycle } from "../lib/taxonomyHelpers";
import { resolveMatter } from "../lib/matterLabel"; // 191 §C
import { useTranslation } from "../i18n";
import {
  api,
  getScheduleOverrides,
  putScheduleOverride,
  deleteScheduleOverride,
} from "../api";
import { CT, RAD, FS, Z } from "../lib/tokens";
import { defaultScheduleAxis, type ScheduleAxis } from "../lib/scheduleAxis";

const SLOT_H = 14;

// 58 §5d — THE DAY DELIMITATION, and what actually happened to it.
//
// Laurent reports it was visible before. NOTHING REMOVED IT: this file,
// ScheduleGrid, TeamScheduleView and PlacesView are untouched in the last ten
// commits, which the brief measured and which is true. A THEME removed it.
//
// Every hairline and every column tint in these four files was written as
// `rgba(255,255,255,.0x)` — WHITE at 1.5% to 6%. On a dark surface that is a
// faint light line and the grid reads as columns. `Theme.vars["--bs"]` on this
// box holds `#ffffff` ("Light") and `#f0f3f5` ("WATT", the firm's own), and
// `isDarkTheme` correctly flags both as light — so white-on-near-white is
// nothing at all, and the columns run together with no delimitation of any
// kind. 22 values across the four files, all of them.
//
// They are now `color-mix` over `--tp` / `--br`, which invert with the theme:
// the same faint line on dark, a faint DARK line on light. This is also
// CLAUDE.md §4.9 — hardcoded colours are forbidden — applied to code that
// predates the rule.

// 58 §5a/§5d — THE WHOLE WEEK. This skipped Saturday and Sunday and then
// collected FOURTEEN of what was left, so "my next two weeks" was really
// fourteen working days — nearly three calendar weeks — and the grid's 7|7
// split with its gap column fell in the middle of the second week rather than
// on a week boundary. With the axis at seven, fourteen consecutive days ARE two
// weeks and the gap lands where the header always claimed it did.
//
// A day nobody works still renders: it shows an empty column, which is the
// truthful answer and the one that lets a firm paint it.
function getWorkingDays(locale: any) {
  const fmt = new Intl.DateTimeFormat(locale || "en-GB", { weekday: "short" });
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const days = [];
  const cursor = new Date(today);
  for (let n = 0; n < SCHED_DAYS.length * 2; n++) {
    const iso = cursor.toISOString().slice(0, 10);
    const label =
      fmt.format(cursor) +
      " " +
      String(cursor.getDate()).padStart(2, "0") +
      "/" +
      String(cursor.getMonth() + 1).padStart(2, "0");
    days.push({
      iso,
      label,
      schedDayIdx: schedDayIndex(cursor.getDay()),
      isToday: iso === todayIso,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function getDaySlots(
  collab: any,
  schedDayIdx: any,
  overrides: any,
  dateIso: any,
  axis: ScheduleAxis,
) {
  const key = `${collab.id}:${dateIso}`;
  if (overrides[key]) return overrides[key];
  const ws = normalizeWeeklySchedule(collab.weeklySchedule, axis.slots);
  return ws[schedDayIdx]?.slots || new Array(axis.slots).fill(null);
}

// 58 §5b — the firm's axis, mirrored module-side exactly as the paint state
// below is. The helpers in this file run outside the component (they are
// driven from pointer handlers through these refs), so they cannot read a
// prop; this is the file's own answer to that, not a new mechanism.
let _svAxis = defaultScheduleAxis();

// Module-level paint state (survives re-renders)
let _svPainting = false;
let _svDrag: any = null;
let _svOverrides: Record<string, any> = {};
let _svSetOverrides: any = null;
let _svPaintMode = "onsite";
let _svCurrentUser: any = null;
let _svCanEditAny = false;
let _svRerender: any = null;

function svSaveScroll() {
  const el = document.querySelector(".content");
  return el ? el.scrollTop : 0;
}
function svRestoreScroll(p: any) {
  requestAnimationFrame(() => {
    const el = document.querySelector(".content");
    if (el) el.scrollTop = p;
  });
}

// Stage 3 D20 invariant: an override row exists IFF its slots differ from
// the collaborator's base weeklySchedule for that weekday. A day that
// equals base — including all-null or fully-reverted — must not persist.
function _svBaseForDay(collabId: any, dateIso: any) {
  const dw = new Date(dateIso).getDay();
  const schedDayIdx = schedDayIndex(dw);
  if (_svBaseGetter) return _svBaseGetter(collabId, schedDayIdx);
  return new Array(_svAxis.slots).fill(null);
}

function _svSlotsEqualBase(slots: any, base: any) {
  const A = slots || new Array(_svAxis.slots).fill(null);
  const B = base || new Array(_svAxis.slots).fill(null);
  if (A.length !== B.length) return A.length === 0 && B.length === 0;
  for (let i = 0; i < A.length; i++) {
    if ((A[i] || null) !== (B[i] || null)) return false;
  }
  return true;
}

function svPaintSlot(collabId: any, dateIso: any, slotIdx: any) {
  // Stage 3 D-A — client gate: permission to paint is enforced at the cell
  // (canPaint in JSX). Belt-and-braces here so a mid-drag permission revoke
  // can't keep writing.
  if (collabId !== _svCurrentUser && !_svCanEditAny) return;

  const key = `${collabId}:${dateIso}`;
  const base = _svBaseForDay(collabId, dateIso);
  const prev = _svOverrides[key] ? [..._svOverrides[key]] : null;
  // Start from prev if any, else from base — so the override is built
  // incrementally and never auto-seeded as a base copy.
  const cur = prev ? [...prev] : [...base];

  // Eraser reverts a single slot to its base value (not null). That makes
  // "erase everything" terminate at base-equal, which then triggers a
  // DELETE of the row (no all-null residue, no base-identical residue).
  //
  // 157A §10(B) — "notworking" is NOT the eraser, and the difference is the
  // whole point: the eraser gives back base[slotIdx], this writes an explicit
  // null REGARDLESS of base. Against a WORKING base that is a real difference
  // and it persists; against a non-working base it collapses to equal-base and
  // the row is deleted — which is also right, since there is nothing to mark.
  const val =
    _svPaintMode === "eraser"
      ? base[slotIdx] || null
      : _svPaintMode === "notworking"
        ? null
        : _svPaintMode;

  if (cur[slotIdx] === val) return;
  cur[slotIdx] = val;

  // Decide PUT vs DELETE based on the invariant: cur === base → DELETE.
  const equalsBase = _svSlotsEqualBase(cur, base);
  const scrollPos = svSaveScroll();

  // Update client state. If cur equals base, drop the key entirely so the
  // 15-day grid falls back to base (the whole point of D20).
  let nov;
  if (equalsBase) {
    nov = { ..._svOverrides };
    delete nov[key];
  } else {
    nov = { ..._svOverrides, [key]: cur };
  }
  _svOverrides = nov;
  if (_svSetOverrides) _svSetOverrides(nov);

  // Stage 3 D-A — roll back on non-2xx so a server failure doesn't leave a
  // phantom paint (or a phantom deletion) on screen. Each slot snapshots
  // its own `prev`.
  const rollback = () => {
    const rb = { ..._svOverrides };
    if (prev == null) delete rb[key];
    else rb[key] = prev;
    _svOverrides = rb;
    if (_svSetOverrides) _svSetOverrides(rb);
    if (_svRerender) _svRerender();
  };
  // Phase 30 (D-3) — rollback existed; the failure toast was missing.
  const failed = (e: any) => {
    rollback();
    window.__lxToast?.("Schedule: " + (e?.message || "save failed"), "error");
  };
  try {
    if (equalsBase) {
      deleteScheduleOverride(collabId, dateIso).catch(failed);
    } else {
      putScheduleOverride(collabId, dateIso, cur).catch(failed);
    }
  } catch {}
  svRestoreScroll(scrollPos);
}

function svCellDown(collabId: any, dateIso: any, slotIdx: any, e: any) {
  e.preventDefault();
  e.stopPropagation();
  _svPainting = true;
  _svDrag = {
    collabId,
    dateIso,
    startSlot: slotIdx,
    currentSlot: slotIdx,
    mouseX: e.clientX,
    mouseY: e.clientY,
  };
  // Stage 3 D20 — no auto-create on click. The override is built lazily by
  // svPaintSlot itself (which starts from base when prev is null and only
  // persists when the result differs from base).
  svPaintSlot(collabId, dateIso, slotIdx);
  if (_svRerender) _svRerender();
}

let _svBaseGetter: any = null;

function svCellMove(collabId: any, dateIso: any, slotIdx: any, e: any) {
  if (!_svPainting) return;
  e.preventDefault();
  e.stopPropagation();
  if (!_svDrag || _svDrag.collabId !== collabId || _svDrag.dateIso !== dateIso)
    return;
  _svDrag.mouseX = e.clientX;
  _svDrag.mouseY = e.clientY;
  if (slotIdx !== _svDrag.currentSlot) {
    _svDrag.currentSlot = slotIdx;
    svPaintSlot(collabId, dateIso, slotIdx);
    if (_svRerender) _svRerender();
  }
}

function svMouseUp() {
  if (_svPainting) {
    _svPainting = false;
    _svDrag = null;
    if (_svRerender) _svRerender();
  }
}

const EVT_OVERLAY_TYPES = new Set(["AUD", "INTRO", "PRO", "REU"]);

function timeToSlot(timeStr: any) {
  if (!timeStr) return -1;
  const [h, m] = timeStr.split(":").map(Number);
  return (h - _svAxis.start) * 2 + (m >= 30 ? 1 : 0);
}

const ScheduleView = ({
  co,
  sCo,
  currentUser,
  ag,
  ca,
  /* 191 §C — the collective cases, for the overlay's matter name (App already
     holds the list; no fetch added). */
  cc,
  nav,
  schedAxis,
  tryNavCase,
  evtTypes,
  s,
  toast,
  // Phase 39 — click-to-call on the collaborator strip (ring / add-to-call).
  onCallColleague,
  callActive = false,
}: any) => {
  const { t, locale } = useTranslation();
  /* Phase 30 Stage-3 (C-1) — schedule cells carry firm-visible (REU) events
     whose case is often outside the viewer's scope; the bare nav() landed on
     a blank CaseView. C85 guard instead. */
  const _navCase = tryNavCase || ((kind: any, id: any) => (nav(kind, id), true));
  const _workLocList = useTaxonomyList("work_location");
  const _LOC_CLR = (code: any) => getWorkLocationMeta(code, _workLocList).color;
  const _LOC_LBL = (code: any) => getWorkLocationMeta(code, _workLocList).label;
  const _LOC_CYCLE = workLocationCycle(_workLocList);
  const internals = co.filter((c: any) => !c.coType || c.coType === "internal");
  const days = getWorkingDays(locale);
  const [overrides, setOverrides] = useState<Record<string, any>>({});
  const [locked, setLocked] = useState(true);
  const [online, setOnline] = useState(new Set());
  useEffect(() => {
    let stopped = false;
    const fetchOnline = async () => {
      try {
        const list = await api<{ id: number }[]>("/presence");
        if (!stopped) setOnline(new Set((list || []).map((x: any) => x.id)));
      } catch {}
    };
    fetchOnline();
    const t = setInterval(fetchOnline, 30000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, []);
  const [paintMode, setPaintMode] = useState("onsite");
  const [tick, setTick] = useState(0);
  const rerender = useCallback(() => setTick((t) => t + 1), []);

  // Schedule is the person's own declared presence. Anyone (any role with
  // lateral.schedule) may edit their OWN row; editing a COLLEAGUE's row is
  // the grantable lateral.schedule.edit key — brief 86 §2a-1, reversing
  // Stage 3 D20's admin-only rule so that a secretary can be given it.
  // clientCan already returns true for isAdmin, so admin keeps the right
  // here for the same reason it keeps it on the server: the overlay, not a
  // branch. The server gate (requireScheduleEditAccess) asks the identical
  // question, so this offers exactly what the API will accept.
  const _perms = usePermissions();
  const canEditAny = _perms.can("lateral.schedule.edit");
  const editing = !locked && currentUser;

  // Build event overlay map: evMap[collabId:dateIso:slotIdx] = { type, title, caseRef, caseName, caId, evId }
  const evMap: Record<string, any> = {};
  const dateSet = new Set(days.map((d) => d.iso));
  (ag || []).forEach((ev: any) => {
    if (!ev.date || !ev.type || !EVT_OVERLAY_TYPES.has(ev.type)) return;
    const dateIso = ev.date.slice(0, 10);
    if (!dateSet.has(dateIso)) return;
    const assignees = ev.assignedCollaborators || [];
    if (!assignees.length) return;
    const startSlot = ev.time ? timeToSlot(ev.time) : -1;
    if (startSlot < 0 || startSlot >= schedAxis.slots) return;
    // Default 1h (2 slots) per event, max to end of day
    const endSlot = Math.min(startSlot + 2, schedAxis.slots);
    /* 191 §C — the matter through the one resolver: a collective case's event
       names its collective case (it resolved nothing here). */
    const cs = resolveMatter({ caseId: ev.caId, ccId: ev.ccId }, ca, cc);
    assignees.forEach((coId: any) => {
      for (let s = startSlot; s < endSlot; s++) {
        evMap[`${coId}:${dateIso}:${s}`] = {
          type: ev.type,
          title: ev.title || "",
          caseRef: cs?.ref || "",
          caseName: cs?.title || "",
          caId: ev.caId,
          ccId: ev.ccId,
          evId: ev.id,
          isFirst: s === startSlot,
        };
      }
    });
  });

  // Sync module-level state
  _svAxis = schedAxis;
  _svOverrides = overrides;
  _svSetOverrides = setOverrides;
  _svPaintMode = paintMode;
  _svCurrentUser = currentUser;
  _svCanEditAny = canEditAny;
  _svRerender = rerender;
  _svBaseGetter = (collabId: any, schedDayIdx: any) => {
    const c = co.find((x: any) => x.id === collabId);
    const ws = normalizeWeeklySchedule(c?.weeklySchedule, schedAxis.slots);
    return ws[schedDayIdx]?.slots || new Array(schedAxis.slots).fill(null);
  };

  // Phase asset-reservation C3 — load overrides from the server. One GET
  // for the entire 14-working-day window across every collaborator (cross-
  // user visibility is the whole point of this checkpoint). The prior
  // browser-local lx:schedOverride keys are abandoned at cutover — see
  // recon RQ5.
  useEffect(() => {
    // Item-5 fix — the `stopped` guard (mirroring the presence effect above)
    // makes this mount fetch idempotent: under React StrictMode (dev build,
    // main.jsx) effects fire twice, and without the guard the first invocation's
    // resolved fetch still called setOverrides AFTER cleanup → a redundant
    // second render that flickered the heavy grid. The guard drops the stale
    // setState so the grid populates once.
    let stopped = false;
    const pull = async () => {
      if (!days.length) return;
      const from = days[0].iso;
      const to = days[days.length - 1].iso;
      try {
        const rows = (await getScheduleOverrides(from, to)) as
          | { collaboratorId: number; date: string; slots: unknown }[]
          | null;
        const ov: Record<string, any> = {};
        for (const r of rows || []) {
          ov[`${r.collaboratorId}:${r.date}`] = r.slots;
        }
        if (!stopped) setOverrides(ov);
      } catch {
        if (!stopped) setOverrides({});
      }
    };
    pull();
    // Phase 30 (C) — another user's override paint streams in live.
    let tmr: any = null;
    const h = (e: any) => {
      if (e?.detail?.type !== "schedule") return;
      clearTimeout(tmr);
      tmr = setTimeout(pull, 400);
    };
    window.addEventListener("lx:sse", h);
    return () => {
      stopped = true;
      clearTimeout(tmr);
      window.removeEventListener("lx:sse", h);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Window mouseup
  useEffect(() => {
    window.addEventListener("mouseup", svMouseUp, true);
    return () => window.removeEventListener("mouseup", svMouseUp, true);
  }, []);

  const dragRange =
    _svPainting && _svDrag
      ? (() => {
          const lo = Math.min(_svDrag.startSlot, _svDrag.currentSlot);
          const hi = Math.max(_svDrag.startSlot, _svDrag.currentSlot);
          return slotToTime(lo) + " – " + slotToTime(hi + 1);
        })()
      : null;

  const modeBtn = (mode: any, label: any, color: any) => {
    const sel = paintMode === mode;
    return (
      <button
        type="button"
        key={mode}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          fontSize: FS.s10,
          fontWeight: 600,
          fontFamily: "inherit",
          borderRadius: RAD.r6,
          cursor: "pointer",
          background: sel ? color : "var(--bc)",
          color: sel ? "var(--tp)" : "var(--ts)",
          border: `2px solid ${sel ? color : "var(--br)"}`,
          opacity: sel ? 1 : 0.7,
          transition: "all .12s",
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setPaintMode(mode);
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: RAD.r2,
            background: color,
            display: "inline-block",
            border: "1px solid var(--br)",
          }}
        />
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        overflowX: "auto",
        userSelect: _svPainting ? "none" : undefined,
        position: "relative",
      }}
    >
      <style>{`@keyframes lxMePulse{0%,100%{box-shadow:0 0 0 1px ${CT.c_4A7CFF},0 0 4px rgba(74,124,255,.5)}50%{box-shadow:0 0 0 1px ${CT.c_4A7CFF},0 0 10px rgba(74,124,255,.95)}}.lxMeBadge{animation:lxMePulse 2s ease-in-out infinite;border-color:${CT.c_4A7CFF} !important}@keyframes lxOnPulse{0%,100%{box-shadow:0 0 0 2px ${CT.c_34D399},0 0 4px rgba(52,211,153,.55)}50%{box-shadow:0 0 0 2px ${CT.c_34D399},0 0 10px rgba(52,211,153,.95)}}.lxOnline{animation:lxOnPulse 2s ease-in-out infinite}.lxMeBadge.lxOnline{animation:lxMePulse 2s ease-in-out infinite,lxOnPulse 2s ease-in-out infinite}`}</style>
      <h4
        style={{
          fontSize: FS.s12,
          fontWeight: 600,
          color: "var(--tp)",
          marginTop: 22,
          marginBottom: 10,
          /* 179 A.1 — MEASURED, NOT CHANGED
             (`scripts/drive-177-sticky-header.js --site=sched`). `left: 0`
             pins this heading against the `overflowX: "auto"` wrapper above,
             which pads nothing, so its edge rests exactly at that box's edge
             under a horizontal scroll — it never pins against `.content`, and
             `.content`'s top-padding cancellation has no business here. Its
             band holds nothing else (`paintedOver: []` scrolled), so the absent
             background paints nothing through it. */
          position: "sticky",
          left: 0,
        }}
      >
        {t("schedule.header.nextTwoWeeks")}
      </h4>
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
          alignItems: "center",
          fontSize: FS.s10,
          position: "sticky",
          left: 0,
          zIndex: Z.panelPopover,
          background: "var(--bd)",
          paddingBottom: 4,
        }}
      >
        {editing && (
          <>
            {modeBtn(
              "onsite",
              t("teamSchedule.onsite", { fallback: "Onsite" }),
              _LOC_CLR("onsite"),
            )}
            {modeBtn(
              "thirdparty",
              t("teamSchedule.thirdparty", { fallback: "Third Party" }),
              _LOC_CLR("thirdparty"),
            )}
            {modeBtn(
              "home",
              t("teamSchedule.home", { fallback: "Home" }),
              _LOC_CLR("home"),
            )}
            {/* 157A §10(B) — the palette was onsite · thirdparty · home,
                every one of them "working somewhere". This is the way to say
                "I do not work this slot on this day", which is what Laurent
                was reaching for when he pressed Gomme. */}
            {/* RULED 2026-08-31 — the swatch is the SAME lookup the other
                three use. It read CT.c_666 while `notworking` was absent from
                WORK_LOCATION_DEFAULTS; now that it is there, `_LOC_CLR`
                answers red for it and the palette has one source of colour. */}
            {modeBtn(
              "notworking",
              t("teamSchedule.notworking", { fallback: "Not working" }),
              _LOC_CLR("notworking"),
            )}
            {/* RULED 2026-09-01 — THE ERASER'S SELECTED STATE WAS UNREADABLE.
                `modeBtn` paints the selected button `background: color` with
                `color: var(--tp)`, and this passed the raw hex CT.c_666. A
                fixed mid-grey cannot work for both themes because `--tp` is
                the one thing that DOES flip: on the light theme it is dark
                text on a dark grey fill.
                The other three pass `_LOC_CLR(...)`, a firm-theme colour, and
                the eraser cannot join them — it is not a work location and has
                no `_LOC_CLR` entry by design (adding one would put a tool in
                the location taxonomy). So it takes the neutral firm-theme
                SURFACE token instead: `--bh` is the app's raised/hover
                surface, it is derived per theme, and it is what `.colTag:hover`
                and the rest of the app already pair with `--tp`. Selected now
                reads as a lifted neutral chip in both themes, with the label
                legible, and no raw hex. */}
            {modeBtn(
              "eraser",
              t("teamSchedule.eraser", { fallback: "Eraser" }),
              "var(--bh)",
            )}
          </>
        )}
        {!editing &&
          _LOC_CYCLE.map((loc) => (
            <span
              key={loc}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: RAD.r2,
                  background: _LOC_CLR(loc),
                  display: "inline-block",
                }}
              />
              <span style={{ color: "var(--ts)" }}>
                {t("teamSchedule." + loc, { fallback: _LOC_LBL(loc) })}
              </span>
            </span>
          ))}
        {/* RULED 2026-08-31 — THE LEGEND NAMES THE COLOUR IT ALREADY SHOWS.
            §10(B) put an explicit not-worked slot on the board in var(--rd),
            and this legend listed only `_LOC_CYCLE` (the three working
            locations) plus "Off" — so a viewer who cannot edit saw a red block
            with no key. This is not a UI addition: the legend exists, and it
            had acquired a colour it did not name. It reads from the same
            `_LOC_CLR("notworking")` the palette button and the cell do, so
            there is still one source for the colour.
            It sits BESIDE the three rather than inside the map, because
            `_LOC_CYCLE` is the work-location taxonomy — a list of places
            someone works — and not-working is deliberately not a member of
            it (see WORK_LOCATION_DEFAULTS). Same reason it is not in the
            paint cycle. */}
        {!editing && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: RAD.r2,
                background: _LOC_CLR("notworking"),
                display: "inline-block",
              }}
            />
            <span style={{ color: "var(--ts)" }}>
              {t("teamSchedule.notworking", { fallback: "Not working" })}
            </span>
          </span>
        )}
        {/* "Off" is the ABSENCE of a shift — a slot outside the base
            schedule. Distinct from the red above, which is a decision
            somebody recorded, and that distinction is the whole reason §10(B)
            needed a rendering of its own. */}
        {!editing && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: RAD.r2,
                background: "var(--bc)",
                border: "1px solid var(--br)",
                display: "inline-block",
              }}
            />
            <span style={{ color: "var(--tm)" }}>
              {t("schedule.legend.off", { fallback: "Off" })}
            </span>
          </span>
        )}
        {currentUser && (
          <LockButton
            {...({
              locked: locked,
              onToggle: () => setLocked((v: any) => !v),
              style: { marginLeft: "auto" },
            } as any)}
          />
        )}
      </div>

      {dragRange && _svDrag && (
        <div
          style={{
            position: "fixed",
            left: _svDrag.mouseX + 14,
            top: _svDrag.mouseY - 10,
            background: "var(--bc)",
            border: "1px solid var(--br)",
            borderRadius: RAD.r6,
            padding: "3px 8px",
            fontSize: FS.s10,
            fontWeight: 600,
            fontFamily: "'JetBrains Mono',monospace",
            color:
              paintMode === "eraser"
                ? "var(--tm)"
                : _LOC_CLR(paintMode) || "var(--ac)",
            zIndex: Z.topMenu,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {dragRange}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `36px repeat(${SCHED_DAYS.length}, minmax(${Math.max(internals.length * 24, 80)}px, 1fr)) 32px repeat(${SCHED_DAYS.length}, minmax(${Math.max(internals.length * 24, 80)}px, 1fr))`,
          gridTemplateRows: `auto auto repeat(${schedAxis.slots}, ${SLOT_H}px)`,
          minWidth: 36 + SCHED_DAYS.length * 2 * Math.max(internals.length * 24, 80) + 32,
        }}
      >
        {/* dayCol: week 1 → cols 2..N+1, the gap column, week 2 → cols N+3..
            Derived from SCHED_DAYS.length; the comment that stood here still
            described the five-day layout of two axes ago. */}
        {/* Row 1: date headers */}
        <div />
        {days.map((day, di) => (
          <div
            key={"dh-" + di}
            style={{
              gridColumn: di < SCHED_DAYS.length ? di + 2 : di + 3,
              textAlign: "center",
              fontSize: FS.s9,
              fontWeight: 600,
              padding: "4px 2px",
              color: day.isToday ? "var(--ac)" : "var(--tp)",
              background: day.isToday
                ? "var(--as)"
                : di >= SCHED_DAYS.length
                  ? "color-mix(in srgb, var(--tp) 2%, transparent)"
                  : "transparent",
              borderBottom: "1px solid color-mix(in srgb, var(--br) 60%, transparent)",
            }}
          >
            {day.label}
          </div>
        ))}
        {/* Row 2: collaborator badges */}
        <div />
        {days.map((day, di) => (
          <div
            key={"ini-" + di}
            style={{
              gridColumn: di < SCHED_DAYS.length ? di + 2 : di + 3,
              display: "flex",
              gap: 0,
              padding: "3px 0",
              alignItems: "center",
              borderBottom: "1px solid var(--br)",
              background: day.isToday
                ? "var(--as)"
                : di >= SCHED_DAYS.length
                  ? "color-mix(in srgb, var(--tp) 2%, transparent)"
                  : "transparent",
            }}
          >
            {internals.map((c: any) => {
              const isMe = c.id === currentUser;
              return (
                <div
                  key={c.id}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {/* Phase 31 (C5) — shared photo-capable InitialsBadge;
                      title/className (lxMeBadge/lxOnline pulses) pass through
                      ...rest, and the schedule palette (--bc fill, hairline
                      border, no static ring) rides the style override.
                      Phase 39 — CallAvatar wraps it so clicking a colleague
                      rings them (or adds them to a live call); "me" stays a
                      plain badge. */}
                  <CallAvatar
                    person={c}
                    isMe={isMe}
                    onCall={onCallColleague}
                    callActive={callActive}
                    size={20}
                    fontSize={8}
                    title={
                      pname(c) +
                      (isMe ? " (you)" : "") +
                      (online.has(c.id) ? " · online" : "")
                    }
                    className={
                      [
                        isMe ? "lxMeBadge" : "",
                        online.has(c.id) ? "lxOnline" : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined
                    }
                    style={{
                      background: "var(--bc)",
                      border: "1px solid var(--br)",
                      color: isMe ? "var(--tp)" : "var(--tm)",
                      boxShadow: "none",
                    }}
                  />
                </div>
              );
            })}
          </div>
        ))}

        {/* Slot rows */}
        {Array.from({ length: schedAxis.slots }, (_, slotIdx) => {
          const isHour = slotIdx % 2 === 0;
          return [
            <div
              key={"tl-" + slotIdx}
              style={{
                gridRow: slotIdx + 3,
                gridColumn: 1,
                fontSize: FS.s7,
                color: "var(--tm)",
                fontFamily: "'JetBrains Mono',monospace",
                textAlign: "right",
                paddingRight: 4,
                lineHeight: SLOT_H + "px",
              }}
            >
              {isHour ? `${schedAxis.start + slotIdx / 2}:00` : ""}
            </div>,
            ...days.map((day, di) => (
              <div
                key={"c-" + di + "-" + slotIdx}
                style={{
                  gridRow: slotIdx + 3,
                  gridColumn: di < SCHED_DAYS.length ? di + 2 : di + 3,
                  display: "flex",
                  gap: 0,
                  /* 157A §10 COPY (2026-09-01) — THE HOUR LINE MOVES TO THE
                     CELL, and it has to: it was drawn HERE, on the day column,
                     so it crossed every filled cell in the row and cut a run
                     into slices no radius could heal. `ScheduleGrid.tsx:530`
                     puts `borderTop` on the CELL precisely so a filled cell can
                     answer "none" (:506-511). Each cell's border spans its full
                     box including the 2px gutters, so the line stays continuous
                     across the day wherever it is not suppressed. */
                  background: day.isToday
                    ? "color-mix(in srgb, var(--ac) 4%, transparent)"
                    : di >= SCHED_DAYS.length
                      ? "color-mix(in srgb, var(--tp) 1.5%, transparent)"
                      : "transparent",
                }}
              >
                {internals.map((collab: any) => {
                  const slots = getDaySlots(
                    collab,
                    day.schedDayIdx,
                    overrides,
                    day.iso,
                    schedAxis,
                  );
                  const evKey = `${collab.id}:${day.iso}:${slotIdx}`;
                  const evEntry = evMap[evKey];
                  const baseLoc = slots[slotIdx] || null;
                  /* `loc` (= evEntry ? "thirdparty" : baseLoc) stood here and
                     is gone: `_fillAt(slotIdx)` below computes the same answer
                     and one more case, so keeping a second, narrower name for
                     "what is in this slot" is how the chrome ended up gated on
                     the wrong one twice. One name, one meaning. */
                  const isMe = collab.id === currentUser;
                  /* 157A §10 — the base value BESIDE the effective one. Two
                     things need it, and neither can be told from `baseLoc`
                     alone (which is the override's value where one exists):
                       (A) a slot with nothing to erase must not accept a click
                           as though it did — svPaintSlot's early return at the
                           top is correct and stays, but silently absorbing the
                           click is what made a working eraser look broken;
                       (B) an EXPLICIT not-worked slot must not look like a
                           base non-working one, or a decision is
                           indistinguishable from an absence. Only the explicit
                           case can be told apart, and it is exactly the case
                           that persists: a null against a WORKING base. */
                  const _baseSlots = _svBaseForDay(collab.id, day.iso);
                  const _baseVal = (_baseSlots && _baseSlots[slotIdx]) || null;
                  const _curVal = baseLoc || null;
                  const nothingToErase =
                    editing && paintMode === "eraser" && _curVal === _baseVal;
                  const explicitOff = _curVal === null && _baseVal !== null;
                  const canPaint =
                    editing && !evEntry && (isMe || canEditAny) && !nothingToErase;

                  /* 157A §10(B) RULED (2026-09-01) — A NOT-WORKING CELL WEARS
                     THE SAME CHROME AS A LOCATION CELL; ONLY THE FILL DIFFERS.
                     What stood here computed the run from `loc`, which is null
                     for an explicitly not-worked slot, so `isFirst`/`isLast`
                     were both falsy and `br` fell to 0: a red run got NO
                     rounded ends at all while every colour run got them, and
                     the two also disagreed about the `.lxMeCell` ring and the
                     1px side rails below (both were gated on `loc`). That is
                     the ragged edge — three differences, not one, and none of
                     them a colour.
                     So the run is keyed on WHAT FILLS the cell rather than on
                     whether it is a location: consecutive not-worked slots
                     round at the ends of THEIR run, and an onsite→not-working
                     boundary is a boundary because the two keys differ. A slot
                     with no shift at all (no override, no base) stays null and
                     keeps no chrome, which is right — there is nothing there.
                     THE KEY IS A TAXONOMY CODE, NOT A SENTINEL. It returns
                     "notworking" rather than a private `__off`, so the fill
                     colour below is `_LOC_CLR(fill)` for every case and the
                     red needs no branch of its own — if it needed one the copy
                     would be incomplete. Nothing can collide with it: the
                     store writes NULL for a not-worked slot (svPaintSlot),
                     never the string. */
                  const _fillAt = (i: number) => {
                    if (i < 0 || i >= schedAxis.slots) return null;
                    const evAt =
                      i === slotIdx
                        ? evEntry
                        : evMap[`${collab.id}:${day.iso}:${i}`];
                    if (evAt) return "thirdparty";
                    const cur = slots[i] || null;
                    if (cur) return cur;
                    const bse = (_baseSlots && _baseSlots[i]) || null;
                    return bse ? "notworking" : null;
                  };
                  const fill = _fillAt(slotIdx);
                  const isFirst = fill && fill !== _fillAt(slotIdx - 1);
                  const isLast = fill && fill !== _fillAt(slotIdx + 1);
                  const isOnly = isFirst && isLast;
                  const br = isOnly
                    ? 3
                    : isFirst
                      ? "3px 3px 0 0"
                      : isLast
                        ? "0 0 3px 3px"
                        : 0;
                  const hasOverride =
                    overrides[`${collab.id}:${day.iso}`] != null;

                  const evTip = evEntry
                    ? `${evEntry.type}: ${evEntry.title}${evEntry.caseName ? " — " + evEntry.caseName : ""}${evEntry.caseRef ? " (" + evEntry.caseRef + ")" : ""}`
                    : null;
                  const baseTip =
                    pname(collab) +
                    " " +
                    slotToTime(slotIdx) +
                    (baseLoc
                      ? " " +
                        t("teamSchedule." + baseLoc, {
                          fallback: _LOC_LBL(baseLoc),
                        })
                      : " " + t("schedule.legend.off", { fallback: "Off" })) +
                    (hasOverride
                      ? " " +
                        t("schedule.override_suffix", {
                          fallback: "(override)",
                        })
                      : "");
                  const slotTip = explicitOff
                    ? baseTip +
                      " — " +
                      t("teamSchedule.notworking", { fallback: "Not working" })
                    : baseTip;

                  /* ═══ 157A §10 — THIS CELL IS A COPY OF ScheduleGrid's ═══
                     RULED 2026-09-01: the override grid renders its slots
                     exactly as the base weekly grid does, only smaller. What
                     stood here was ONE div doing both jobs — chrome and fill —
                     which is why it scalloped: every slot drew its own box, so
                     a run read as a stack of cells instead of one block.
                     The model is `ScheduleGrid.tsx:505-550` and it is two divs:
                       OUTER  the grid cell — borderTop (suppressed when
                              filled, :530 + :506-511), and `padding: "0 2px"`
                              (:534) for the side gutter.
                       INNER  the fill — `_LOC_CLR(loc)` SOLID at opacity 1
                              (:541-547) with `borderRadius: br` (:546), which
                              rounds at the run's ends only.
                     Kept from this grid because they are its own geometry, not
                     chrome: the smaller slot height (SLOT_H 14 via the row
                     template at :677), the per-collaborator flex subdivision
                     of the day column, and the 3px radius that is this grid's
                     scaled equivalent of the base grid's 6px.
                     REMOVED, because the model has no such thing and both were
                     what broke the run into slices: the `.lxMeCell` inset ring
                     + outline, which boxed EVERY cell of my own column
                     individually, and the 1px `--tp` side rails beside it. The
                     column is still identified — its header avatar carries the
                     pulsing `lxMeBadge`.
                     REMOVED for the same reason: `opacity: isMe ? 1 : 0.35` on
                     a FILLED cell. That is the "washed out" — three columns in
                     four rendered every fill at 35%. The model's rule is that
                     a filled slot is the solid taxonomy colour at opacity 1,
                     and the ghosting it does use (:547's 0.3) is for a slot
                     that is NOT filled. */
                  return (
                    <div
                      key={collab.id}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: "100%",
                        boxSizing: "border-box",
                        // MODEL ScheduleGrid.tsx:530 — a filled slot has no top
                        // edge, which is what merges a run into one block.
                        borderTop: fill
                          ? "none"
                          : isHour
                            ? "1px solid color-mix(in srgb, var(--br) 60%, transparent)"
                            : "none",
                        // MODEL ScheduleGrid.tsx:534.
                        padding: "0 2px",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          // MODEL ScheduleGrid.tsx:541-545 — the solid taxonomy
                          // colour. `fill` IS a taxonomy code, so the
                          // not-working red resolves through the same lookup
                          // as the other three and needs no branch.
                          background: fill ? _LOC_CLR(fill) : "transparent",
                          // MODEL ScheduleGrid.tsx:546.
                          borderRadius: br,
                          // MODEL ScheduleGrid.tsx:547 — never a tint.
                          opacity: 1,
                          // Brief 86 §2a-1 — "do not offer what the server will
                          // refuse". A cell the viewer may not paint is
                          // READONLY, per the Agenda view's pattern: no
                          // handler, no affordance cursor, colours kept.
                          cursor: evEntry
                            ? "pointer"
                            : canPaint
                              ? "crosshair"
                              : "default",
                          position: "relative",
                          overflow: "visible",
                        }}
                        onMouseDown={
                          canPaint
                            ? (e) => svCellDown(collab.id, day.iso, slotIdx, e)
                            : undefined
                        }
                        onMouseMove={
                          canPaint
                            ? (e) => svCellMove(collab.id, day.iso, slotIdx, e)
                            : undefined
                        }
                        onClick={
                          evEntry && (evEntry.caId || evEntry.ccId)
                            ? () => {
                                // C-1 — scroll queues only on actual nav.
                                // 191 §C — a collective case's event jumps to
                                // its collective case, the same guarded jump.
                                if (_navCase(evEntry.caId ? "ca" : "cc", evEntry.caId || evEntry.ccId))
                                  queueScrollToEv(evEntry.evId);
                              }
                            : undefined
                        }
                        title={evTip || slotTip}
                      >
                        {evEntry && evEntry.isFirst && (
                          <span
                            style={{
                              position: "absolute",
                              top: 1,
                              left: 1,
                              fontSize: FS.s6,
                              fontWeight: 700,
                              lineHeight: 1,
                              color: "rgba(0,0,0,.55)",
                              pointerEvents: "none",
                              whiteSpace: "nowrap",
                              zIndex: Z.stickyHead,
                              fontFamily: "'JetBrains Mono',monospace",
                            }}
                          >
                            {evEntry.type}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )),
          ];
        })}
      </div>

      {/* My Base Schedule */}
      {currentUser &&
        (() => {
          const me = co.find((c: any) => c.id === currentUser);
          if (!me) return null;
          const others = internals
            .filter((c: any) => c.id !== currentUser)
            .map((c: any) => ({
              name: pname(c),
              schedule: normalizeWeeklySchedule(c.weeklySchedule),
            }));
          const onBaseChange = (nws: any) => {
            const nco = co.map((c: any) =>
              c.id === currentUser ? { ...c, weeklySchedule: nws } : c,
            );
            s(SK.co, nco, sCo);
          };
          return (
            <div style={{ marginTop: 24 }}>
              <h4
                style={{
                  fontSize: FS.s12,
                  fontWeight: 600,
                  color: "var(--tp)",
                  marginBottom: 10,
                }}
              >
                {t("schedule.header.baseWeekly")}
              </h4>
              {/* Phase 25.1 r3#4: rounded pane (app-wide .dc convention) so the
                  base schedule matches the rounded-corner panes elsewhere. */}
              <div className="detail-card">
                <ScheduleGrid
                  schedAxis={schedAxis}
                  schedule={normalizeWeeklySchedule(me.weeklySchedule)}
                  onChange={onBaseChange}
                  otherSchedules={others}
                  readOnly={false}
                  canUnlock={true}
                  scheduleId={"sched-" + currentUser}
                />
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default ScheduleView;
